// Copyright: IQGeo Limited 2010-2019
import L from 'leaflet';
import myw from 'myWorld/base/core';

/* Extend marker so that we calculate size and honour alignment directives */
myw.TextMarker = L.Marker.extend({
    options: {
        minFontSize: 4
    },

    initialize(latlng, options) {
        let style =
            // line-height:80%" +
            `position:absolute;font-size:${options.fontSize}px; font-family:${options.fontFamily}; margin:0px; line-height : 1; color:${options.fontColor}`;

        // CSS rotation is clockwise from the x+ axes
        // ENH: Add smarts to ensure text is not 'hanging upside down'. There are good reasons
        // for not doing this but also reasons for doing it.
        if (options['angle']) {
            const angle = options['angle'];
            style += `;${this.cssRotate(angle)}`;
        }

        if (options['bcolor'] && options['bcolor'] != 'none') {
            style += `;background-color : ${options['bcolor']}`;
        }

        if (options['border']) {
            style += `;border-style:solid;border-width:${options['border']}px`;
        }

        const html = `<div style="${style}">${options.text}</div>`;

        // ENH Calculate iconSize width based on font size.
        options['icon'] = new L.DivIcon({
            iconSize: [3000, 0],
            iconAnchor: [0, 0],
            className: 'dummy',
            html: html
        });

        L.Marker.prototype.initialize.call(this, latlng, options);
    },

    /**
     * Creates icon if appropriate
     * Overriden to ensure marker is only added when inside visible zoom range - to improve performance
     */
    onAdd() {
        const z = this._map.getZoom();
        if (z < this.options.min_vis || z > this.options.max_vis) {
            //out of zoom range - text should not be displayed
        } else {
            //in zoom range - add to map
            if (!this._actualFontSize) this._calculateActualFontSize();

            if (this._actualFontSize > this.options.minFontSize) {
                L.Marker.prototype.onAdd.call(this, this._map);
                this._updateFontSize();
                this._updateAnchor();
            }
        }
    },

    /**
     * Opacity is not implemented for text markers
     */
    setOpacity() {},

    /**
     * Overridden to add check for _icon
     */
    _removeIcon() {
        if (this._icon) {
            L.Marker.prototype._removeIcon.call(this);
        }
        this._icon = null;
    },

    /**
     * Overridden to add check for _icon
     */
    bringToBack() {
        if (this._icon && this._icon.parentElement) L.Marker.prototype.bringToBack.call(this);
    },

    /* Create CSS fragment to rotate text in a browser neutral manner
     */
    cssRotate(angle) {
        if (L.Browser.vml) {
            const rad = (angle * Math.PI) / 180,
                costheta = Math.cos(rad),
                sintheta = Math.sin(rad);

            return `filter: progid:DXImageTransform.Microsoft.Matrix(M11=${costheta} , M12=${-sintheta}, M21=${sintheta}, M22=${costheta}, SizingMethod='auto expand');`;
        } else {
            return `transform : rotate(${angle}deg)`;
        }
    },

    update() {
        L.Marker.prototype.update.call(this);
        if (this.options.worldSize && this._icon) {
            this._updateFontSize();
            this._updateAnchor();
        }
        this._onZoom();
    },

    /**
     * If map zoom level is less than than minimum zoom, or greater than maximum zoom, hide the text marker
     */
    _onZoom() {
        const z = this._map.getZoom();
        this._calculateActualFontSize();
        if (
            z < this.options.min_vis ||
            z > this.options.max_vis ||
            this._actualFontSize < this.options.minFontSize
        ) {
            //hide the icon
            if (this._icon) this._icon.childNodes[0].hidden = true;
        } else {
            // create or unhide the icon
            if (!this._icon) this.onAdd(); //create

            if (this._icon) this._icon.childNodes[0].hidden = false; //unhide if exists
        }
    },

    /**
     * If in world units then update the text size and also recalculate the alignment.
     * In worldsize, a user specified size of 10 will mean that the letter 'm' has width approximately 10metres
     * when viewed at the equator.
     * @private
     */
    _updateFontSize() {
        const child_element = this._icon.childNodes[0];
        if (!child_element) return;

        if (this.options.worldSize) {
            const fontSizePt = this.calculateFontSize();

            child_element.style.fontSize = `${fontSizePt}pt`;
            //padding should be relative to font size and 1/10th looks good at all zoom levels
            child_element.style.padding = `${fontSizePt / 10}pt`;
            this._actualFontSize = fontSizePt;
        } else {
            child_element.style.fontSize = `${this.options.fontSize}pt`;
            child_element.style.padding = '5px';
            this._actualFontSize = this.options.fontSize;
        }
    },

    _calculateActualFontSize() {
        if (this.options.worldSize) {
            this._actualFontSize = this.calculateFontSize();
        } else {
            this._actualFontSize = this.options.fontSize;
        }
    },

    /**
     * Calculates font size in pt
     */
    calculateFontSize() {
        const z = this._map.getZoom();
        const ns = Math.pow(2, z - 17);
        return this.options.fontSize * ns;
    },

    /**
     * Update the position of the text box to match the required alignment.
     * The position is composed of two additive components:
     *       Component that centres the text box. This ensures that the rotation is applied around the centre.
     *       Component that reflects the alignment and offseting options. This component needs to be rotated according to
     *       the rotation of the text.
     * @private
     */
    _updateAnchor() {
        const child_element = this._icon.childNodes[0],
            width = child_element.clientWidth,
            height = child_element.clientHeight;
        if (!child_element) return;

        let ns = 1;
        if (this.options.worldSize) {
            const z = this._map.getZoom();
            ns = Math.pow(2, z - 17);
        }

        // Calculate the alignment-offset component
        let dx = 0;
        switch (this.options.halign) {
            case 'left':
                dx = width / 2 + this.options.hOffset * ns;
                break;
            case 'centre':
            case 'center':
                dx = this.options.hOffset * ns;
                break;
            case 'right':
                dx = -(width / 2 + this.options.hOffset * ns);
        }

        let dy = 0;
        switch (this.options.valign) {
            case 'top':
                dy = height / 2 + this.options.vOffset * ns;
                break;
            case 'middle':
                dy = this.options.vOffset * ns;
                break;
            case 'bottom':
                dy = -(height / 2 + this.options.vOffset * ns);
        }

        // Now rotate aligment-offset component
        const theta = (this.options.angle * Math.PI) / 180,
            costheta = Math.cos(theta),
            sintheta = Math.sin(theta),
            dx2 = costheta * dx - sintheta * dy,
            dy2 = sintheta * dx + costheta * dy;

        child_element.style.marginLeft = `${-width / 2 + dx2}px`;
        child_element.style.marginTop = `${-height / 2 + dy2}px`;
    }
});
